import { CurrentUserDto } from '../../../../../types/UserDtos';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { isAnyCycleAdmin, isDecisionMaker } from '../../../services/User';

const JUST_LAUNCHED_LOCAL_STORAGE_KEY = 'JustLaunchedLink';
const ONE_DAY = 24 * 60 * 60 * 1000;

const getLatestLaunchedUpdate = (lastUpdated: number | null, now: number) =>
    lastUpdated !== null && lastUpdated <= now ? lastUpdated : 0;

export const hasUpdateJustLaunched = (lastUpdated: number | null, now: number, lastVisited: number) =>
    lastUpdated !== null && lastUpdated > lastVisited && lastUpdated <= now;

export const shouldShowJustLaunched = (
    lastUpdated: number | null,
    now: number,
    lastVisited: number,
    lastDismissedAt: number,
    currentUser: CurrentUserDto
) => {
    const latestUpdate = getLatestLaunchedUpdate(lastUpdated, now);
    const isDismissed = latestUpdate > 0 && lastDismissedAt >= latestUpdate;
    const relevantUser = isRelevantUser(currentUser);

    if (isDismissed || !relevantUser) {
        return false;
    }

    return now - lastVisited < ONE_DAY || hasUpdateJustLaunched(lastUpdated, now, lastVisited);
};

const isRelevantUser = (currentUser: CurrentUserDto) => {
    const cycleAdmin = isAnyCycleAdmin(currentUser);
    const decisionMaker = isDecisionMaker(currentUser, false);
    const importantManager = currentUser.reports.length >= 5;

    return decisionMaker || cycleAdmin || importantManager;
};

export type JustLaunchedHook = ReturnType<typeof useJustLaunched>;
export const useJustLaunched = (lastUpdated: Date, currentUser: CurrentUserDto) => {
    const [state, setState] = useLocalStorage(JUST_LAUNCHED_LOCAL_STORAGE_KEY, { lastVisit: 0, lastDismissedAt: 0 });
    const lastDismissedAt = state.lastDismissedAt ?? 0;

    return {
        shouldShow: shouldShowJustLaunched(
            lastUpdated.getTime(),
            Date.now(),
            state.lastVisit,
            lastDismissedAt,
            currentUser
        ),
        hasUpdate: hasUpdateJustLaunched(lastUpdated.getTime(), Date.now(), state.lastVisit),
        markAsVisited: () => setState({ ...state, lastVisit: Date.now() }),
        dismiss: () => setState({ ...state, lastDismissedAt: Date.now() }),
    };
};
